import React, { useCallback, useMemo } from 'react';
import { Components } from 'formiojs';
import type { ExtendedComponentSchema } from 'formiojs';
import { EhsFormioReactComponent, SimpleConditional } from '../';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { DateInput, DateTimeInput } from '../../../form';
import type { Component } from '@formio/react';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type FormioDateTimeInputConstructor = {
  timepicker: boolean;
};
export type FormioDateTimeInputSettings = {
  timepicker: boolean;
};

// This component was renamed from DateTimePicker to DateTimeInput
// Therefore leaving FormioDateTimeInputType as ehsdatetimepicker since it's already used.
export const FormioDateTimeInputType = 'ehsdatetimepicker';

export class EhsFormioDateTimeInput extends EhsFormioReactComponent<
  FormioDateTimeInputSettings,
  FormioDateTimeInputConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: FormioDateTimeInputType,
      label: 'common.date',
      key: FormioDateTimeInputType,
      timepicker: false,
      ...extend
    });
  }

  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-DateTimeinput',
      group: 'advanced',
      icon: 'calendar',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioDateTimeInput.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'checkbox',
          input: true,
          key: 'timepicker',
          label: 'Add Time',
          tooltip: 'Show (Date- &) Time input .',
          weight: 30
        },
        SimpleConditional
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private ehsTimepicker = false;

  extractFields(component: Component<FormioDateTimeInputConstructor>): void {
    this.ehsTimepicker = component.timepicker;
    this.shouldSetValue = true;
    this.dataForSetting = this._data[component.key as string];
  }

  attachEhsReact(): [
    (props: EhsFormioReactProps<FormioDateTimeInputSettings>) => JSX.Element,
    FormioDateTimeInputSettings
  ] {
    return [DateInputCmp, { timepicker: this.ehsTimepicker }];
  }
}

//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

/**
 * The Logic for the Formio-Component.
 *
 * @param params formio {@link FormioDateTimeInputSettings} properties
 */
function DateInputCmp({
  value,
  onChange,
  label,
  readOnly,
  settings
}: EhsFormioReactProps<FormioDateTimeInputSettings>): JSX.Element {
  const onChangeWrapper = useCallback(
    (selectedDates: Date | null): void => {
      onChange(selectedDates?.toISOString() ?? '');
    },
    [onChange]
  );
  const wrapperValue = useMemo(() => {
    return (value ?? '') == '' ? null : new Date(value);
  }, [value]);
  return useMemo(() => {
    if (settings.timepicker) {
      return (
        <DateTimeInput
          onChange={onChangeWrapper}
          value={wrapperValue}
          label={label}
          disabled={readOnly}
        />
      );
    } else {
      return (
        <DateInput
          onChange={onChangeWrapper}
          value={wrapperValue}
          label={label}
          disabled={readOnly}
        />
      );
    }
  }, [label, onChangeWrapper, readOnly, settings.timepicker, wrapperValue]);
}
